import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CSVUploadInstructions,
  fetchApi,
  FileData,
  FormButtons,
  getRowsFromRawString,
  identifyErrors,
  makeResetCache,
  notifyError,
  notifyResponse,
  PageTitle,
  ReportUploadStatus,
  Section,
  selectUser,
} from 'gossamer-front-end';
import { CSVRow } from 'gossamer-unified';
import {
  AdminInstitutionGroupMemberAddRequest,
  InstitutionGroupMemberUpload,
  InstitutionGroupMemberUploadRow,
  institutionGroupMemberValidator,
  MemberInvite,
  UserView,
} from 'cb-model';
import { useParams } from 'react-router-dom';

export const BulkAddMembersToGroup = (): React.JSX.Element => {
  const params = useParams();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [rows, setRows] = React.useState<CSVRow<InstitutionGroupMemberUploadRow>[] | null>(null);
  const [errors, setErrors] = React.useState<string[][] | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const resetCache = makeResetCache(dispatch);

  const handleChange = (newValue: string) => {
    try {
      const rows = getRowsFromRawString(InstitutionGroupMemberUpload, newValue);
      setRows(rows);
      setErrors(identifyErrors(rows, institutionGroupMemberValidator));
    } catch (e) {
      setRows(null);
      setErrors(null);
      notifyError(e, 'An error occurred while reading the CSV file');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const invites = errors
        .map((error) => error.length === 0)
        .map((valid, index) => (valid ? rows[index] : null))
        .filter((row) => !!row);
      const response = await fetchApi('POST', `/main/institution-group-member-add`, {
        invites: invites.map(
          (invite) =>
            ({
              emailAddress: invite.email_address,
              addToInstitutionGroupIds: [parseInt(params.key)],
              registrationTaskDefinitionId: Number(invite.registration_task_definition_id),
            }) as MemberInvite,
        ),
        institutionId: parseInt(user.orgPath, 10),
      } as AdminInstitutionGroupMemberAddRequest);
      setRows([]);
      setErrors([]);
      resetCache(UserView.id);
      notifyResponse(response);
    } catch (e) {
      notifyError(e, 'An error occurred while adding members to the group');
    }
    setSubmitting(false);
  };

  return (
    <>
      <PageTitle title="Bulk Upload Members to this Group" subtitle="Using a CSV file as described below" />

      <Section>
        <CSVUploadInstructions entity={InstitutionGroupMemberUpload} />
        <div className="mb20px"></div>
        <div>
          Choose a CSV file:&nbsp;
          <FileData onChange={handleChange} />
        </div>
      </Section>

      <Section>
        {!!rows && (
          <div>
            <ReportUploadStatus errors={errors} />
            <FormButtons handleSubmit={handleSubmit} submitting={submitting} />
          </div>
        )}
      </Section>
    </>
  );
};
